import { useEffect, useRef } from "react"
import { useAtomValue } from "jotai"
import { lastSelectionFromClickAtom } from "@/store/photo/photos-selection-state"
import { PhotoRecord } from "@/lib/catalog/types"
import { Virtualizer } from "@tanstack/react-virtual"
import { CLUSTER_THUMBNAIL_WIDTH_PX, PHOTO_THUMBNAIL_WIDTH_PX } from "./filmstrip.config"
import { isPhotoFullscreenAtom } from "@/store/ui-views-state"

interface UseScrollToCurrentPhotoProps {
	isShown: boolean
	currentPhoto: PhotoRecord | null
	isImporting: boolean
	isShowingClusters: boolean
	coverPhotoIds: string[]
	displayedPhotoIds: string[]
	clustersVirtualizer: Virtualizer<HTMLDivElement, Element>
	photosVirtualizer: Virtualizer<HTMLDivElement, Element>
	currentClusterId: string | undefined
	currentPhotoClusterIds: string[]
}

/**
 * Hook that automatically scrolls the filmstrip to center the currently selected photo.
 *
 * This hook handles scrolling for both filmstrip views:
 * - Main filmstrip: scrolls to center the selected photo in the horizontal virtualized list
 * - Stacked photos: when in cluster view, scrolls the bottom filmstrip to center the selected photo within the cluster
 *
 * The hook only triggers scrolling when:
 * - The filmstrip is visible
 * - A photo is selected
 * - Not currently importing
 * - The last selection was NOT from a click (to avoid interfering with manual scrolling)
 */
export function useScrollToCurrentPhoto({
	isShown,
	currentPhoto,
	isImporting,
	isShowingClusters,
	coverPhotoIds,
	displayedPhotoIds,
	clustersVirtualizer,
	photosVirtualizer,
	currentClusterId,
	currentPhotoClusterIds,
}: UseScrollToCurrentPhotoProps) {
	const lastSelectionFromClick = useAtomValue(lastSelectionFromClickAtom)
	const isPhotoFullscreen = useAtomValue(isPhotoFullscreenAtom)
	const wasPhotoFullscreenRef = useRef(false)

	const scrollToCurrentPhoto = (currentPhoto: PhotoRecord) => {
		// Handle main filmstrip/clusters
		const photoIds = isShowingClusters ? coverPhotoIds : displayedPhotoIds
		const virtualizer = isShowingClusters ? clustersVirtualizer : photosVirtualizer
		const currentPhotoId = isShowingClusters ? currentClusterId : currentPhoto.id
		const currentIndex = photoIds.findIndex((id) => id === currentPhotoId)

		if (currentIndex !== -1) {
			// Center the selection immediately for keyboard navigation
			const scrollElement = virtualizer.scrollElement
			if (scrollElement) {
				const itemSize = isShowingClusters
					? CLUSTER_THUMBNAIL_WIDTH_PX
					: PHOTO_THUMBNAIL_WIDTH_PX
				const containerWidth = scrollElement.clientWidth
				const itemStart = currentIndex * itemSize
				const itemCenter = itemStart + itemSize / 2
				const containerCenter = containerWidth / 2

				// Calculate scroll position to center the item
				const targetScrollLeft = itemCenter - containerCenter

				// Scroll immediately without animation
				scrollElement.scrollLeft = Math.max(0, targetScrollLeft)
			}
		}

		// Handle stacked photos (when in cluster view)
		if (isShowingClusters && currentPhotoClusterIds.includes(currentPhoto.id)) {
			const stackedContainer = document.getElementById("stacked_container")
			const stackedPhoto = document.getElementById("stacked_" + currentPhoto.id)

			if (stackedContainer && stackedPhoto) {
				const containerRect = stackedContainer.getBoundingClientRect()
				const photoRect = stackedPhoto.getBoundingClientRect()

				// Calculate positions relative to container
				const photoLeft = photoRect.left - containerRect.left + stackedContainer.scrollLeft
				const containerWidth = containerRect.width
				const photoWidth = photoRect.width

				// Center the selected stacked photo for keyboard navigation
				const photoCenter = photoLeft + photoWidth / 2
				const containerCenter = containerWidth / 2
				const targetScrollLeft = photoCenter - containerCenter

				// Always use immediate scrolling without animation
				stackedContainer.scrollLeft = Math.max(0, targetScrollLeft)
			} else if (stackedPhoto) {
				// Fallback to basic scroll if container not found
				stackedPhoto.scrollIntoView({
					behavior: "auto",
					block: "nearest",
					inline: "center",
				})
			}
		}
	}
	useEffect(() => {
		if (!isShown || !currentPhoto) {
			return
		}

		// Don't scroll if the last selection was from a click
		if (lastSelectionFromClick) {
			return
		}

		scrollToCurrentPhoto(currentPhoto)
	}, [currentPhoto?.id, isImporting, currentPhotoClusterIds, isShown])

	useEffect(() => {
		if (!isShown || !currentPhoto) {
			return
		}

		scrollToCurrentPhoto(currentPhoto)
	}, [displayedPhotoIds, isShowingClusters])

	useEffect(() => {
		const didExitPhotoFullscreen = wasPhotoFullscreenRef.current && !isPhotoFullscreen
		wasPhotoFullscreenRef.current = isPhotoFullscreen

		if (!didExitPhotoFullscreen || !isShown || !currentPhoto) {
			return
		}

		requestAnimationFrame(() => {
			scrollToCurrentPhoto(currentPhoto)
		})
		// Only react to the fullscreen exit edge; the scroll helper reads the latest render state.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isPhotoFullscreen, isShown, currentPhoto?.id])
}
